import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import useChartZoom from './useChartZoom';
import ZoomControl from './ZoomControl';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from '../../lib/charts/highcharts';
import ChartFrame from './ChartFrame';
import { cardinalFor, sectorColor, WIND_ZONES, SAFE_BAND, CALM_MAX, windTickPositions } from '../../lib/charts/history';
import { formatStationTime } from '../../lib/charts/time';
import { LINE_COLORS } from '../../lib/charts/lineColors';
import { zoomingFor } from './zooming';

export { LINE_COLORS };

/**
 * Wind timeline: speed spline + gust spline with safety zones + direction
 * barbs in a dedicated bottom lane, each barb coloured by the station's
 * safe sectors. Stock chart: All/1h/3h zoom, navigator, drag-to-pan.
 *
 * xAxis.ordinal:false is REQUIRED: with an ordinal axis the windbarb
 * series' x-data corrupts Stock's pan math and every drag is silently
 * cancelled (reference app post-mortem, highstock OrdinalAxis.onChartPan).
 */
const tooltipHeader = (ms) => formatStationTime(ms, 'DD.MM.YYYY HH:mm');

/**
 * Where the All/1h/3h control renders. `zoomTarget` undefined → inline above the chart
 * (default); an element → portalled there (e.g. the card header, so the plot gets the
 * full card height); null → the parent intends a portal target that hasn't mounted yet.
 */
export function renderZoom(shared, own, zoomLabels, zoomTarget) {
    if (shared) return null;
    const node = <ZoomControl value={own.zoom} onChange={own.setZoom} labels={zoomLabels} />;
    if (zoomTarget === undefined) return <div className="flex justify-end mb-2">{node}</div>;
    return zoomTarget ? createPortal(node, zoomTarget) : null;
}


// Same thresholds on both lines, mirroring the rose bins: <0.5 slate, 0.5–2 teal, then the line's own
// colour up to 5 (blue for speed, green for gusts, so they stay tellable apart), amber to 7, red above.
const zonesFor = (ownColor) => [
    { value: CALM_MAX, color: LINE_COLORS.calm },
    { value: SAFE_BAND[0], color: LINE_COLORS.light },
    { value: WIND_ZONES[0], color: ownColor },
    { value: WIND_ZONES[1], color: LINE_COLORS.caution },
    { color: LINE_COLORS.danger },
];

export function buildWindSeries(points, sectors, labels) {
    const speed = []; const gust = []; const barbs = [];
    for (const p of points) {
        // Direction rides on the speed point so the tooltip has it at EVERY bucket
        // (barbs are grouped/sparse and don't take part in hover).
        speed.push({ x: p.t, y: p.windSpeed, custom: { dir: p.windDirection } });
        gust.push([p.t, p.windGust]);
        // No barb in calm air (< CALM_MAX): direction is meaningless there and Highcharts would draw an oversized circle.
        if (p.windSpeed !== null && p.windDirection !== null && p.windSpeed >= CALM_MAX) {
            barbs.push({ x: p.t, value: p.windSpeed, direction: p.windDirection, color: sectorColor(p.windDirection, sectors) });
        }
    }
    return [
        {
            name: labels.direction, type: 'windbarb', yAxis: 1, vectorLength: 24, lineWidth: 1.5,
            showInLegend: false, enableMouseTracking: false,
            // Barbs only: let Stock keep the visible barb count roughly constant across zoom.
            dataGrouping: { enabled: true, groupPixelWidth: 30 },
            data: barbs,
        },
        { name: labels.wind, type: 'spline', yAxis: 0, color: LINE_COLORS.wind, lineWidth: 2, connectNulls: false, zones: zonesFor(LINE_COLORS.wind), data: speed },
        {
            name: labels.gust, type: 'spline', yAxis: 0, color: LINE_COLORS.safe, lineWidth: 2, connectNulls: false,
            zones: zonesFor(LINE_COLORS.safe),
            data: gust,
        },
    ];
}

// Zoom is our own ZoomControl (see useChartZoom); Highcharts' rangeSelector stays off.
export const STOCK_CHROME = {
    rangeSelector: { enabled: false },
    navigator: { enabled: true, height: 36 },
    scrollbar: { enabled: false },
};

const WindTimelineChart = ({ points, sectors, labels, zoomLabels, shared, zoomTarget, plotBands, zoomResetKey, height = 320, loading, error, emptyMessage, errorMessage, retryLabel, onRetry }) => {
    // zoomResetKey (range/station) resets to All; new points alone keep the preset and slide it.
    const own = useChartZoom(zoomResetKey ?? points, points);
    // `shared` = { chartRef, xAxisEvents } from useSharedZoom: no own zoom control, no own navigator.
    const chartRef = shared ? shared.chartRef : own.chartRef;
    const xAxisEvents = shared ? shared.xAxisEvents : own.xAxisEvents;
    const options = useMemo(() => ({
        chart: { height, backgroundColor: 'transparent', panning: { enabled: true, type: 'x' }, zooming: zoomingFor(shared) },
        ...STOCK_CHROME,
        navigator: { enabled: !shared, height: 36 },
        title: { text: undefined },
        xAxis: {
            type: 'datetime', crosshair: true, ordinal: false, events: xAxisEvents, plotBands,
            dateTimeLabelFormats: { hour: '%H:%M', day: '%a %H:%M', week: '%d %b', month: '%d %b' },
        },
        yAxis: [
            {
                // Same axis chrome as the temperature/pressure charts: unit title at the top, axis line,
                // ticks and labels in the series colour, a fixed tick count so the grid reads at every zoom.
                title: { text: 'm/s', align: 'high', rotation: 0, offset: 0, textAlign: 'left', x: 3, y: -3, style: { color: 'var(--chart-wind)', fontSize: '10px' } },
                // 2 m/s step on normal days (one gridline always means the same thing); 5 / 10 m/s only in storms
                // so labels never crowd. The positioner's `max` already includes maxPadding (gust headroom).
                min: 0, maxPadding: 0.2, opposite: false, showLastLabel: true,
                tickPositioner(min, max) { return windTickPositions(max); },
                tickColor: 'var(--chart-wind)', lineColor: 'var(--chart-wind)', tickWidth: 1, tickLength: 6, lineWidth: 1,
                labels: { x: -3, style: { color: 'var(--chart-wind)', fontSize: '10px' } }, height: '80%',
                // Shaded ideal-takeoff band so anyone can see at a glance when the wind was within launch limits.
                plotBands: [{
                    from: SAFE_BAND[0], to: SAFE_BAND[1], color: 'var(--chart-safe-band)', zIndex: 0,
                    label: { text: `${labels.safeBand || ''} · ${SAFE_BAND[0]}–${SAFE_BAND[1]} m/s`, align: 'right', verticalAlign: 'top', x: -8, y: 12, style: { color: 'var(--chart-safe)', fontSize: '10px', fontWeight: '500' } },
                }],
            },
            { top: '82%', height: '18%', offset: 0, min: 0, max: 1, gridLineWidth: 0, labels: { enabled: false }, title: { text: undefined }, visible: false },
        ],
        tooltip: {
            shared: true, useHTML: true, borderRadius: 6, padding: 8,
            formatter() {
                const lines = [`<span style="font-size:10px;color:var(--chart-text-subtle)">${tooltipHeader(Number(this.x))}</span>`];
                let dirLine = null;
                for (const p of this.points || []) {
                    const dot = `<span style="color:${p.color}">●</span>`;
                    const v = p.y === null || p.y === undefined ? '—' : p.y.toFixed(1);
                    lines.push(`${dot} ${p.series.name}: <b>${v} m/s</b>`);
                    const dir = p.point && p.point.options && p.point.options.custom ? p.point.options.custom.dir : undefined;
                    if (dir !== null && dir !== undefined && dirLine === null) {
                        // Own line: arrow turned downwind (like the barbs) and tinted by the station's safe sectors.
                        const arrow = `<span style="display:inline-block;width:14px;text-align:center;color:${sectorColor(dir, sectors)};transform:rotate(${Math.round(dir) + 90}deg);font-size:13px;line-height:1">➤</span>`;
                        dirLine = `${arrow} ${labels.direction}: <b>${cardinalFor(dir)}</b> <span style="color:var(--chart-text-subtle)">${Math.round(dir)}°</span>`;
                    }
                }
                if (dirLine) lines.push(dirLine);
                return lines.join('<br>');
            },
        },
        plotOptions: {
            series: {
                animation: { duration: 600 },
                marker: { enabled: false, states: { hover: { enabled: true } } },
                // Stock groups by default and would erase the outage gaps. Barbs re-enable it per-series.
                dataGrouping: { enabled: false },
            },
        },
        legend: { enabled: true, margin: 8 },
        accessibility: { description: 'Wind timeline: speed, gust and direction barbs over time' },
        series: buildWindSeries(points, sectors, labels),
    }), [points, sectors, labels, height, xAxisEvents, shared, plotBands]);

    return (
        <div>
            {renderZoom(shared, own, zoomLabels, zoomTarget)}
            <ChartFrame ariaLabel={labels.wind} height={height} loading={loading} error={error}
                        empty={!points || points.length < 2} emptyMessage={emptyMessage}
                        errorMessage={errorMessage} retryLabel={retryLabel} onRetry={onRetry}>
                <HighchartsReact ref={chartRef} highcharts={Highcharts} constructorType="stockChart" options={options} />
            </ChartFrame>
        </div>
    );
};

export default WindTimelineChart;
